import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Check, Shield, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/PageHeader";
import { PullToRefresh } from "@/components/shared/PullToRefresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { nigerianBanks } from "@/lib/nigerianBanks";

interface BankAccount {
  id: string;
  account_name: string;
  account_number_masked: string;
  bank_name: string;
  bank_code?: string;
  account_type: string;
  is_default: boolean;
  status: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
}

export default function BankAccounts() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifiedName, setVerifiedName] = useState<string | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    bank_code: '',
    account_type: 'savings'
  });

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  // Auto-verify when account_number and bank_code are filled
  useEffect(() => {
    setVerifiedName(null);
    setVerificationError(null);

    if (formData.account_number.length === 10 && formData.bank_code) {
      const timer = setTimeout(() => {
        verifyAccount(formData.account_number, formData.bank_code);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.account_number, formData.bank_code]);

  const verifyAccount = async (accountNumber: string, bankCode: string) => {
    setVerifying(true);
    setVerifiedName(null);
    setVerificationError(null);
    try {
      const { data, error } = await supabase.functions.invoke('verify-bank-account', {
        body: { account_number: accountNumber, bank_code: bankCode }
      });

      if (error) throw error;

      if (data.verified) {
        setVerifiedName(data.account_name);
        setFormData(prev => ({ ...prev, account_name: data.account_name }));
      } else {
        setVerificationError(data.error || 'Could not verify account');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError('Verification service unavailable');
    } finally {
      setVerifying(false);
    }
  };

  const fetchBankAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_user_bank_accounts');
      if (error) throw error;
      setAccounts(data || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      toast.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  const { isRefreshing, pullDistance, triggerRefresh } = usePullToRefresh({
    onRefresh: fetchBankAccounts,
  });

  const handleBankChange = (bankCode: string) => {
    const bank = nigerianBanks.find(b => b.code === bankCode);
    if (bank) {
      setFormData(prev => ({ ...prev, bank_name: bank.name, bank_code: bank.code }));
    }
  };

  const handleAddAccount = async () => {
    if (!verifiedName) {
      toast.error('Please verify your account before adding');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please login to add bank account');
        return;
      }

      const { error } = await supabase.rpc('add_bank_account', {
        p_account_name: formData.account_name,
        p_account_number: formData.account_number,
        p_bank_name: formData.bank_name,
        p_bank_code: formData.bank_code || null,
        p_account_type: formData.account_type
      });

      if (error) throw error;

      toast.success('Bank account added & verified successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchBankAccounts();
    } catch (error) {
      console.error('Error adding bank account:', error);
      toast.error('Failed to add bank account');
    }
  };

  const resetForm = () => {
    setFormData({ account_name: '', account_number: '', bank_name: '', bank_code: '', account_type: 'savings' });
    setVerifiedName(null);
    setVerificationError(null);
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      const { error } = await supabase.from('bank_accounts').update({ is_default: true }).eq('id', accountId);
      if (error) throw error;
      toast.success('Default account updated');
      fetchBankAccounts();
    } catch (error) {
      console.error('Error setting default account:', error);
      toast.error('Failed to set default account');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      const { error } = await supabase.from('bank_accounts').delete().eq('id', accountId);
      if (error) throw error;
      toast.success('Bank account removed');
      fetchBankAccounts();
    } catch (error) {
      console.error('Error deleting bank account:', error);
      toast.error('Failed to delete bank account');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
          <div className="h-48 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <PullToRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <PageHeader
        title="Bank Accounts"
        description="Manage your bank accounts for receiving payments from escrow"
        backTo="/seller/dashboard"
        onRefresh={triggerRefresh}
        isRefreshing={isRefreshing}
      />

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>Add your bank accounts to receive payments</CardDescription>
              </div>
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="w-full sm:w-auto">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Bank Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add Bank Account</DialogTitle>
                    <DialogDescription>
                      Select your bank and enter your account number to auto-verify
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>Select Bank</Label>
                      <Select value={formData.bank_code} onValueChange={handleBankChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose your bank" />
                        </SelectTrigger>
                        <SelectContent>
                          {nigerianBanks.map((bank) => (
                            <SelectItem key={bank.code} value={bank.code}>
                              {bank.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="account_number">Account Number</Label>
                      <Input
                        id="account_number"
                        placeholder="Enter 10-digit account number"
                        maxLength={10}
                        value={formData.account_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                      />
                    </div>

                    {/* Verification Status */}
                    {verifying && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying account...
                      </div>
                    )}
                    {verifiedName && (
                      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                        <span>Account Name: <strong>{verifiedName}</strong></span>
                      </div>
                    )}
                    {verificationError && (
                      <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                        <XCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{verificationError}</span>
                      </div>
                    )}

                    <div className="grid gap-2">
                      <Label htmlFor="account_type">Account Type</Label>
                      <Select
                        value={formData.account_type}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="savings">Savings</SelectItem>
                          <SelectItem value="current">Current</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddAccount} disabled={!verifiedName || verifying}>
                      {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                      Add Verified Account
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
        </Card>

        {accounts.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No bank accounts added</h3>
              <p className="text-muted-foreground text-center max-w-md">
                Add a bank account to receive payments from your escrow balance
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {accounts.map((account) => (
              <Card key={account.id}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div className="flex-1 w-full">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{account.account_name}</h3>
                        {account.is_default && (
                          <Badge variant="default" className="text-xs">Default</Badge>
                        )}
                        {account.status === 'verified' ? (
                          <Badge variant="default" className="text-xs">
                            <Check className="mr-1 h-3 w-3" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Pending</Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="text-muted-foreground">Account: {account.account_number_masked}</p>
                        <p className="text-muted-foreground">Bank: {account.bank_name}</p>
                        <p className="text-muted-foreground">Type: {account.account_type}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      {!account.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleSetDefault(account.id)} className="flex-1 sm:flex-none">
                          Set as Default
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleDeleteAccount(account.id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
